import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { getInitialPage, Page, SearchCriteria } from 'src/models/owns/page';
import type { AppThunk } from 'src/store';
import Quotation from '../models/owns/quotation';
import api from '../utils/api';
import { revertAll } from 'src/utils/redux';

const basePath = 'quotations';

interface QuotationState {
  quotations: Page<Quotation>;
  singleQuotation: Quotation;
  loadingGet: boolean;
}

const initialState: QuotationState = {
  quotations: getInitialPage<Quotation>(),
  singleQuotation: null,
  loadingGet: false
};

const slice = createSlice({
  name: 'quotations',
  initialState,
  extraReducers: (builder) => builder.addCase(revertAll, () => initialState),
  reducers: {
    getQuotations(
      state: QuotationState,
      action: PayloadAction<{ quotations: Page<Quotation> }>
    ) {
      const { quotations } = action.payload;
      state.quotations = quotations;
    },
    getSingleQuotation(
      state: QuotationState,
      action: PayloadAction<{ quotation: Quotation }>
    ) {
      const { quotation } = action.payload;
      state.singleQuotation = quotation;
    },
    addQuotation(
      state: QuotationState,
      action: PayloadAction<{ quotation: Quotation }>
    ) {
      const { quotation } = action.payload;
      state.quotations.content = [
        ...state.quotations.content,
        quotation
      ];
    },
    editQuotation(
      state: QuotationState,
      action: PayloadAction<{ quotation: Quotation }>
    ) {
      const { quotation } = action.payload;
      const inContent = state.quotations.content.some(
        (q) => q.id === quotation.id
      );
      if (inContent) {
        state.quotations.content = state.quotations.content.map(
          (quotation1) => {
            if (quotation1.id === quotation.id) {
              return quotation;
            }
            return quotation1;
          }
        );
      } else {
        state.singleQuotation = quotation;
      }
    },
    deleteQuotation(
      state: QuotationState,
      action: PayloadAction<{ id: number }>
    ) {
      const { id } = action.payload;
      const quotationIndex = state.quotations.content.findIndex(
        (quotation) => quotation.id === id
      );
      state.quotations.content.splice(quotationIndex, 1);
    },
    setLoadingGet(
      state: QuotationState,
      action: PayloadAction<{ loading: boolean }>
    ) {
      const { loading } = action.payload;
      state.loadingGet = loading;
    },
    clearSingleQuotation(
      state: QuotationState,
      action: PayloadAction<{}>
    ) {
      state.singleQuotation = null;
    }
  }
});

export const reducer = slice.reducer;

export const getQuotations =
  (criteria: SearchCriteria): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(slice.actions.setLoadingGet({ loading: true }));
      const quotations = await api.post<Page<Quotation>>(
        `${basePath}/search`,
        criteria
      );
      dispatch(slice.actions.getQuotations({ quotations }));
    } finally {
      dispatch(slice.actions.setLoadingGet({ loading: false }));
    }
  };

export const getSingleQuotation =
  (id: number): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setLoadingGet({ loading: true }));
    const quotation = await api.get<Quotation>(`${basePath}/${id}`);
    dispatch(slice.actions.getSingleQuotation({ quotation }));
    dispatch(slice.actions.setLoadingGet({ loading: false }));
  };

export const editQuotation =
  (id: number, quotation): AppThunk =>
  async (dispatch) => {
    const quotationResponse = await api.patch<Quotation>(
      `${basePath}/${id}`,
      quotation
    );
    dispatch(
      slice.actions.editQuotation({
        quotation: quotationResponse
      })
    );
  };

export const addQuotation =
  (quotation): AppThunk =>
  async (dispatch) => {
    const quotationResponse = await api.post<Quotation>(basePath, quotation);
    dispatch(
      slice.actions.addQuotation({ quotation: quotationResponse })
    );
    return quotationResponse.id;
  };

export const deleteQuotation =
  (id: number): AppThunk =>
  async (dispatch) => {
    const quotationResponse = await api.deletes<{ success: boolean }>(
      `${basePath}/${id}`
    );
    const { success } = quotationResponse;
    if (success) {
      dispatch(slice.actions.deleteQuotation({ id }));
    }
  };

export const clearSingleQuotation = (): AppThunk => async (dispatch) => {
  dispatch(slice.actions.clearSingleQuotation({}));
};

export default slice;
